import {ApiService} from '@core/services/api.service';
import {Injectable} from '@angular/core';
import {IResponseApi} from '@core/models';
import {map} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {CONVERSATION_TYPE, IConversationFilterParam} from '@features/conversation/models/conversation.model';
import {IMessageSearchPayload} from '@features/conversation/models/utility-message.model';
import {CHANNEL} from '@features/conversation/models/channel.model';
import {ATTACHMENT_UPLOAD_SCOPE} from '@features/conversation/models/image-library.model';
import {parseParams} from '@shared/utils';
import {uriConsts} from '@shared/constants';
import {IUserBusiness} from '@shared/models/user/user.model';
import {
  IConversationAttachmentsPayload,
  IConversationAttachmentsResponse,
} from '@features/conversation/models/conversation-attachment.model';

/**
 * Interface tham số gửi lên API /report/user/tracking
 * Dùng để tracking lượt chăm sóc khách hàng và tốc độ phản hồi của nhân viên
 */
export interface ITrackingUserReportParams {
  pid: string;             // ID Fanpage / Page
  cid: string;             // ID khách hàng (fanpageUserId)
  t: number;               // Loại hội thoại (1 = comment, 2 = inbox)
  msgId?: string;          // ID tin nhắn phản hồi của nhân viên (serverMsg.id); FE13 private reply không gửi
  conversationId?: string; // ID hội thoại để BE tra cứu trực tiếp O(1)
  customerSentAt?: number; // Timestamp (giây) tin nhắn gần nhất của khách để tính tốc độ phản hồi
}

export interface ILoadUsersParams {
  businessIds?: (string | number)[];
  businessId?: string | number;
  limit?: number;
}

export interface ICreateMetaOrderParams {
  conversationId: string;
  eventName: string;
  value: number;
  currency: string;
  createdById: string;
  tagCodes?: string[] | null;
}

export interface IPostItem {
  id: string;
  message?: string;
  story?: string;
  caption?: string;
  /** Public post URL returned by Threads/Meta after synchronization. */
  permalink?: string;
  url?: string;
  created_time?: string;
  postCreatedAt?: number;
  attachments?: {
    data?: Array<{
      media?: { image?: { src: string } };
      subattachments?: { data?: Array<{ media?: { image?: { src: string } } }> };
    }>;
  } | Array<{url?: string; media_url?: string; type?: string; thumbnailUrl?: string}>;
}

export interface IPostManageResponse {
  code: number;
  items: IPostItem[];
  paging?: {
    cursors?: { after?: string; before?: string };
    next?: string;
  };
  messages?: string[];
}

/** Kết quả createpost/createcarouselpost/publishcontainer của Threads. */
export interface IThreadsPublishResult {
  /** false: Meta chưa xử lý xong media, gọi lại publishThreadsContainer với containerId. */
  published: boolean;
  id?: string;
  containerId?: string;
  permalink?: string | null;
  /** SUCCESS/FAILED khi bật chia sẻ sang Instagram Story, null khi không bật. */
  crossShareToIgStatus?: 'SUCCESS' | 'FAILED' | null;
  /** Bài vừa lưu (cùng dạng item postmanage) để chèn ngay vào danh sách. */
  post?: IPostItem;
}

/** Hồ sơ tài khoản Threads đang kết nối. */
export interface IThreadsProfile {
  id: string;
  username: string;
  name: string;
  avatar: string;
  biography: string;
  isVerified: boolean;
}

/** Bài Threads của người khác: nhắc đến, kết quả tìm kiếm, bài công khai của hồ sơ. */
export interface IThreadsMedia {
  id: string;
  text?: string;
  username?: string;
  /** ISO 8601, ví dụ 2026-10-12T10:00:00+0000. */
  timestamp?: string;
  permalink?: string;
  /** TEXT_POST, IMAGE, VIDEO, CAROUSEL_ALBUM, AUDIO hoặc REPOST_FACADE (bài đăng lại, không có nội dung). */
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  /** Các ảnh/video của bài CAROUSEL_ALBUM. */
  children?: {data?: IThreadsMedia[]};
  is_quote_post?: boolean;
  /** Bài gốc của bài đăng lại / trích dẫn; chỉ có {id} khi server không đọc được nội dung. */
  reposted_post?: IThreadsMedia;
  quoted_post?: IThreadsMedia;
}

/** Một trang bài Threads, trang sau lấy bằng paging.cursors.after. */
export interface IThreadsMediaPage {
  items: IThreadsMedia[];
  paging?: {cursors?: {after?: string}; next?: string} | null;
}

/** Địa điểm để gắn vào bài viết Threads. */
export interface IThreadsLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  country: string;
}

/** Hồ sơ Threads công khai; các chỉ số tính trong 7 ngày gần nhất. */
export interface IThreadsPublicProfile {
  username: string;
  name: string;
  avatar: string;
  biography: string;
  isVerified: boolean;
  followerCount: number | null;
  likesCount: number | null;
  repliesCount: number | null;
  quotesCount: number | null;
  repostsCount: number | null;
  viewsCount: number | null;
}

/** Chỉ số trọn đời của một bài Threads. */
export interface IThreadsPostInsights {
  views: number;
  likes: number;
  replies: number;
  reposts: number;
  quotes: number;
  shares: number;
}

export interface IThreadsDemographicItem {
  /** Mã quốc gia (VN, US...), nhóm tuổi (18-24...) hoặc giới tính (F, M, U). */
  key: string;
  value: number;
}

export type ThreadsDemographicDimension = 'country' | 'age' | 'gender';

/** Lý do không có nhân khẩu học: dưới 100 người theo dõi hoặc Meta không trả. */
export type ThreadsDemographicsUnavailableReason = 'NOT_ENOUGH_FOLLOWERS' | 'UNAVAILABLE';

/** Chỉ số tài khoản Threads trong khoảng since/until (Unix giây). */
export interface IThreadsAccountInsights {
  since: number;
  until: number;
  totals: {views: number; likes: number; replies: number; reposts: number; quotes: number};
  viewsByDay: {date: string; value: number}[];
  followersCount: number | null;
  /** Null khi không có dữ liệu, lý do nằm ở demographicsUnavailableReason. */
  demographics: Partial<Record<ThreadsDemographicDimension, IThreadsDemographicItem[]>> | null;
  demographicsUnavailableReason: ThreadsDemographicsUnavailableReason | null;
}

export interface ILockCustomerResponse {
  locked?: number;
  blocked?: number;
  lockedLog?: { userId: string; type: string; time: number };
  displayNameList?: Record<string, { displayName: string }>;
}

export interface ICreatePollPayload {
  pageId: string;
  groupId: string;
  question: string;
  options: string[];
  expiredTime: number;
  allowMultiChoices: boolean;
  allowAddNewOption: boolean;
  hideVotePreview: boolean;
  isAnonymous: boolean;
}

export interface ICreatePollResponse {
  creator: string;
  question: string;
  options: string[];
  joined: boolean;
  closed: boolean;
  poll_id: string | number;
  allow_multi_choices: boolean;
  allow_add_new_option: boolean;
  is_anonymous: boolean;
  poll_type: number;
  created_time: number;
  updated_time: number;
  expiried_time: number;
  is_hide_vote_preview: boolean;
  num_vote: number;
}

export interface IPollDetailOption {
  content: string;
  votes: number;
  voted: boolean;
  voters: string[];
  option_id: number;
}

export interface IGetPollDetailResponse extends Omit<ICreatePollResponse, 'options'> {
  options: IPollDetailOption[];
}

export interface IGetPollDetailPayload {
  pageId: string;
  pollId: string | number;
}

export type ILockPollPayload = IGetPollDetailPayload;

export interface IConversationActionPayload {
  conversationId: string;
  pageId?: string;
  messageId?: string;
  type?: string;
  until?: number;
}

export interface IReminderPayload {
  conversationId?: string;
  customerId: string;
  content: string;
  time: number;
  pageId?: string;
}

// ---------------------------------------------------------------------------
// Response-shape interfaces (khai báo theo BE IndexController.php)
// ---------------------------------------------------------------------------

/**
 * Dữ liệu tin nhắn phân trang được nhúng trong `res.data.message`.
 * - `next` : Elasticsearch search_after cursor (array sort values) từ showDetail.
 * Truthy = còn tin nhắn cũ hơn. Dùng làm `after` cho lần gọi loadOlderMsg đầu tiên.
 * - `nextOlder` : search_after cursor từ loadOlderMsg response (các trang tiếp theo).
 * - `nextNewer` : search_after cursor từ loadNewerMsg / loadAroundMsg response.
 * Giá trị `false` (từ BE) = không còn trang, được normalize thành `null` khi FE nhận.
 */
export interface IMessageListData {
  items: any[];
  next?: any[] | false | null;
  nextOlder?: any[] | false | null;
  nextNewer?: any[] | false | null;
}

/** `res.data` trả về từ `showDetail` (load ban đầu). */
export interface IShowDetailData {
  message: IMessageListData;
  conversation: any;
  customer: any;
  feature: any[];
  postType: number;
  post?: any;
  postMsg?: any[];
  customerGroup?: any[];
  zaloStickers?: any[];
}

/** `res.data` trả về từ `showDetail?act=loadOlderMsg` / `loadNewerMsg`. */
export interface ILoadDirectionalMessagesData {
  message: IMessageListData;
  conversation: any;
  postType: number;
}

/** [§4.1] Một chủ đề marketing (OTN topic) trả về từ BE. */
export interface IMarketingTopicItem {
  id: string;
  name: string;
  title: string;
  businessId: number;
  pageId?: string;
  optInMessageId?: string;
  status: number;
}

/** [§4.1] `res.data` trả về từ `listMarketingTopics`. */
export interface IListMarketingTopicsData {
  items: IMarketingTopicItem[];
  next: unknown[];
}

/** [§4.9] Một dòng log trong hàng đợi gửi kịch bản. */
export interface IScriptQueueLogItem {
  id: string;
  campaignId?: string;
  campaignName?: string;
  actionType?: number;
  scriptScheduleId?: string;
  type?: number;
  createdAt?: number | string;
  scheduledAt?: number | string;
  sentAt?: number | string;
  status?: number;
  sendType?: number;
  sentStatus?: number;
  logResponse?: string;
}

/** [§4.9] Payload `res.scroller` từ BE `/chatbot/scriptschedule/viewlogqueue`. */
export interface IScriptQueueLogScroller {
  items: IScriptQueueLogItem[];
  next?: string | false | null;
}

/** [§4.9] Response xem log queue kịch bản. */
export interface IViewScriptQueueResponse {
  scroller?: IScriptQueueLogScroller;
  code?: number;
  error?: string;
  messages?: string[];
}

// ---------------------------------------------------------------------------
// [§4.6] Product dialog — interfaces
// ---------------------------------------------------------------------------

/** Danh mục sản phẩm Facebook Catalog. */
export interface IProductCatalog {
  id: string;
  name: string;
  product_count: number;
  image_url?: string;
}

/** Sản phẩm con (variant) trong Meta Catalog — sau khi chuyển đổi giá. */
export interface IMetaProductChild {
  id: string;
  name: string;
  price: number;
  sale_price: number;
  color?: string;
  size?: string;
  image_url?: string;
  url?: string;
  quantity_to_sell_on_facebook?: number;
}

/** Nhóm sản phẩm nhóm theo `retailer_product_group_id`. */
export interface IMetaProductGroup {
  id: string;
  name: string;
  image_url?: string;
  childs: IMetaProductChild[];
  minPrice: number;
  maxPrice: number;
}

/** Sản phẩm thô từ BE getmetaproducts (giá là chuỗi kiểu "10.000 ₫"). */
export interface IMetaProductRaw {
  id: string;
  name: string;
  price?: string;
  sale_price?: string;
  color?: string;
  size?: string;
  image_url?: string;
  url?: string;
  quantity_to_sell_on_facebook?: number;
  retailer_product_group_id?: string;
}

/** Sản phẩm từ sàn Ecom (Shopee/Lazada/TikTok) trả về từ BE. */
export interface IEcomProductRaw {
  id: string | number;
  name: string;
  price: string | number;
  currency?: string;
  image?: string;
  stock?: number;
  url?: string;
}

// Response shapes (internal — không export)
interface IGetCatalogResponse {
  data?: IProductCatalog[];
  error?: string;
}

interface IGetMetaProductsResponse {
  data?: IMetaProductRaw[];
  cursors?: { before?: string; after?: string };
  error?: string;
}

interface IGetEcomProductListResponse {
  /** Shopee/Lazada: items trực tiếp */
  items?: IEcomProductRaw[];
  /** TikTok: bọc trong data */
  data?: { items?: IEcomProductRaw[]; next?: string | null };
  total?: number;
  next?: string | number | false | null;
  code?: number;
  error?: string;
}

export interface IEcomVoucherRaw {
  id?: string | number;
  voucherId?: string | number;
  code?: string;
  title?: string;
  name?: string;
  voucherType?: string | number;
  discountType?: string | number;
  discountAmount?: string | number;
  percentage?: string | number;
  percentUsage?: string | number;
  showDate?: string;
  status?: string;
  creationSource?: string;
  targetBuyerSegment?: string;
}

interface IGetEcomVoucherListResponse {
  items?: IEcomVoucherRaw[];
  data?: { items?: IEcomVoucherRaw[]; next?: string | null };
  total?: number;
  next?: string | number | false | null;
  code?: number;
  error?: string;
}

interface IGetProductFromPosResponse {
  items?: IEcomProductRaw[];
  next?: string | number | null;
  error?: string;
}

export interface IAddBankAccountResponse {
    id: string;
    bankCode: string;
    accountNumber: string;
    businessId: string | number;
    createdAt: number;
    accountName: string;
    binCode: string;
}

@Injectable({
  providedIn: 'any'
})
export class ConversationApiService extends ApiService {
  /** FE13 getQuoteMsg: bổ sung chữ/ảnh của tin Shopee chỉ có quoted_msg_id. */
  getShopeeQuoteMessage(params: {pageId: string; msgId: string; conversationId: string; quoteMsgId: string}): Observable<IResponseApi<{
    item?: {reply_to?: Record<string, unknown>};
  }>> {
    return this.postRequest('/chat/shopee/getquotemsg', params);
  }

  /** FE13 reloadFileUrl: lấy lại URL CDN Facebook của attachment fileLoad. */
  reloadFileUrl(params: {msgId: string; fanpageId: string}): Observable<IResponseApi> {
    return this.postRequest('/chat/facebook/reloadfileurl', params);
  }

  /**
   * FE13 reloadmessages: lấy lại attachments của tin Facebook qua Graph API (url CDN mới, link cũ hết hạn),
   * BE lưu đè vào DB. Response v1 `{items: [{id, attachments?}]}` → sau normalize nằm ở `data.items`.
   */
  reloadFacebookMessages(params: {messageIds: string[]; fanpageId: string}): Observable<IResponseApi<{
    items?: Array<{id: string; attachments?: Record<string, any>[]}>;
  }>> {
    return this.postRequest('/chat/facebook/reloadmessages', params);
  }

  createPoll(params: ICreatePollPayload) {
    return this.postRequest<IResponseApi<ICreatePollResponse>>('/chat/createpoll', params);
  }

  getPollDetail(params: IGetPollDetailPayload) {
    return this.postRequest<IResponseApi<IGetPollDetailResponse>>('/chat/getpolldetail', params);
  }

  lockPoll(params: ILockPollPayload) {
    return this.postRequest<IResponseApi<{pollId: string}>>('/chat/lockpoll', params);
  }

  /**
   * API v2: đính kèm của hội thoại gộp theo loại (image, video, audio, file, doc, url).
   * BE nhận `types` dạng chuỗi "image,video" và `next` dạng chuỗi JSON nên gửi form như các API khác.
   * Tài liệu: docs/Module/Chat/Index/conversationattachments.md
   */
  getConversationAttachments(params: IConversationAttachmentsPayload) {
    const {types, next, ...rest} = params;
    return this.postRequest<IResponseApi<IConversationAttachmentsResponse>>('/chat/conversationattachments', {
      ...rest,
      ...(types?.length ? {types: types.join(',')} : {}),
      ...(next?.length ? {next: JSON.stringify(next)} : {}),
    });
  }

  getConversation(params: IConversationFilterParam) {
    return this.postRequest<IResponseApi<any>>('/chat/conversations', {
      ...params
    });
  }

  updateConversation(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/updateviewconversation', {
      ...params
    });
  }

  handleConversationAction(act: string, params: IConversationActionPayload | IMessageSearchPayload) {
    // Gom nhóm các action dùng chung endpoint /chat/showdetail?act=...
    return this.postRequest<IResponseApi>(`/chat/showdetail?act=${act}`, {
      ...params
    });
  }

  /** Zalo Personal thu hồi tin nhắn dùng endpoint riêng, không qua showdetail?act=revoke. */
  undoZaloPersonalMessage(params: Pick<IConversationActionPayload, 'pageId' | 'messageId'>) {
    return this.postRequest<IResponseApi>('/chat/zalopersonal/undomessage', params);
  }

  removeZaloPersonalMessage(params: Pick<IConversationActionPayload, 'pageId' | 'messageId'> & {onlyMe: boolean}) {
    return this.postRequest<IResponseApi>('/chat/zalopersonal/removemessage', params);
  }

  pinMessage(params: IConversationActionPayload) {
    return this.handleConversationAction('pin', params);
  }

  unpinMessage(params: IConversationActionPayload) {
    return this.handleConversationAction('unpin', params);
  }

  getPinMessages(params: IConversationActionPayload) {
    return this.handleConversationAction('pinlist', params);
  }

  searchMessageInConversation(params: IMessageSearchPayload) {
    // Mặc định searchMsg để phòng trường hợp caller không truyền act.
    const act = params?.act || 'searchMsg';
    return this.handleConversationAction(act, params);
  }

  /**
   * [FE13] Zalo Personal — thả/gỡ cảm xúc trên tin nhắn (db.addReaction).
   * reaction: 'LIKE' | 'HEART' | 'HAHA' | 'WOW' | 'CRY' | 'ANGRY' | 'NONE' (gỡ).
   */
  addZlpsReaction(params: { pageId: string; messageId: string; reaction: string }) {
    return this.postRequest<IResponseApi>('/chat/zalopersonal/addreaction', {
      ...params
    });
  }

  listComingReminders(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/reminder?act=coming', {
      ...params
    });
  }

  createReminder(params: IReminderPayload) {
    return this.postRequest<IResponseApi>('/chat/reminder?act=create', {
      ...params
    });
  }

  /**
   * [FE13] Lấy danh sách tin nhắn nhanh theo pageId (có businessId).
   * Mặc định tab = 2 (lọc theo page), truyền tab trong params để đổi sang tab doanh nghiệp.
   */
  getQuickMessages(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/quickmessage/list', {
      tab: 2,
      ...params
    });
  }

  /**
   * [FE13] Lấy danh sách tin nhắn nhanh chỉ theo pageIds, KHÔNG gửi businessId.
   * API list là API của trang Cài đặt: không truyền `status` thì trả cả tin đã tắt.
   */
  getQuickMessagesByPageIds(pageIds: string[], params: Record<string, unknown> = {}) {
    return this.postUserRequest<IResponseApi>('/business/quickmessage/list', {
      tab: 2,
      pageIds,
      ...params,
    });
  }

  getQuickMessageCategories(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/quickmessage/handlequickmsgcategories', {
      ...params
    });
  }

  /** [FE13] Kiểm tra page có thuộc nhóm tin nhắn nhanh nào không. */
  handleQuickMsgGroup(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/quickmessage/handlegroup', {
      ...params
    });
  }

  /** [FE13] Lấy tin nhắn nhanh theo business+groupId. */
  getQuickMsgByBusinessId(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/quickmessage/listbybusiness', {
      ...params
    });
  }

  /** [FE13] Lấy tin nhắn nhanh page-level (fallback khi page ko thuộc group). */
  getAllQuickMessages(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/quickmessage/all', {
      ...params
    });
  }

  getCustomerProfile(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/customer/list', {
      ...params
    });
  }

  getAssignableReceivers(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/index/assignablereceivers', {
      ...params
    });
  }

  getDivisionSettings(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/index/getmodesplit', {
      ...params
    });
  }

  splitConversations(params: { conversationIds: string[]; assigneeId: string }) {
    return this.postRequest<IResponseApi>('/chat/reassigneeconversation', {
      ...params
    });
  }

  /**
   * Gửi tin nhắn — tương đương db.reply() bản cũ.
   * Tự động route đến endpoint đúng theo channel và loại hội thoại (inbox/comment).
   * @param params Payload gửi tin (text, files, fanpageId, recipientId, channel, type...)
   */
  sendMessage(params: Record<string, unknown>) {
    const channel = params['channel'] as number;
    const type = params['type'] as number;
    let url = '/chat/facebook/replycomment';
    if (channel === CHANNEL.ZALO && type === CONVERSATION_TYPE.INBOX) {
      url = '/chat/zalo/replyinbox';
    } else if (channel === CHANNEL.TIKTOK) {
      url = '/chat/tiktok/replyinbox';
    } else if (channel === CHANNEL.TIKTOK_BUSINESS) {
      url = type === CONVERSATION_TYPE.COMMENT ? '/chat/tiktokbusiness/replycomment' : '/chat/tiktokbusiness/replyinbox';
    } else if (channel === CHANNEL.THREADS) {
      url = '/chat/threads/replycomment';
    } else if (channel === CHANNEL.SHOPEE) {
      url = '/chat/shopee/replyinbox';
    } else if (channel === CHANNEL.INSTAGRAM) {
      url = type === CONVERSATION_TYPE.COMMENT ? '/chat/instagram/replycomment' : '/chat/instagram/replyinbox';
    } else if (channel === CHANNEL.LAZADA) {
      url = '/chat/lazada/replyinbox';
    } else if (channel === CHANNEL.TELEGRAM) {
      url = '/chat/telegram/replyinbox';
    } else if (channel === CHANNEL.WHATSAPP) {
      url = '/chat/whatsapp/replyinbox';
    } else if (channel === CHANNEL.ZALO_PERSONAL) {
      url = '/chat/zalopersonal/replyinbox';
    } else if (type === CONVERSATION_TYPE.INBOX) {
      url = '/chat/facebook/replyinbox';
    }
    return this.postRequest<IResponseApi>(url, params);
  }

  /**
   * Gửi private reply từ comment, hoặc thao tác trên comment.
   * Tương đương FE13: db.handleComment()
   * Endpoint: /chat/facebook/handlecomment (FB) | /chat/instagram/handlecomment (IG)
   */
  handleComment(params: Record<string, unknown>) {
    const channel = params['channel'] as number;
    let url = '/chat/facebook/handlecomment';
    if (channel === CHANNEL.INSTAGRAM) {
      url = '/chat/instagram/handlecomment';
    }
    if (channel === CHANNEL.TIKTOK_BUSINESS) {
      url = '/chat/tiktokbusiness/handlecomment';
    }
    if (channel === CHANNEL.THREADS) {
      url = '/chat/threads/handlecomment';
    }
    return this.postRequest<IResponseApi>(url, params);
  }

  /**
   * Chia hội thoại inbox cho nhân viên vừa private reply.
   * Tương đương FE13: db.splitPrivateReply()
   * BE: POST /chat/splitprivatereply
   */
  splitPrivateReply(params: { conversationId: string }) {
    return this.postRequest<IResponseApi>('/chat/splitprivatereply', params);
  }

  /** [§3.D.8] Tìm kiếm sticker Zalo Cá Nhân theo từ khoá — FE13 db.searchStickers() */
  searchStickers(params: { pageId: string; keyword: string }) {
    return this.postRequest<IResponseApi>('/chat/zalopersonal/searchstickers', {...params});
  }

  /**
   * Lấy chi tiết hội thoại (messages, customer, features).
   * Tương đương showDetailConversation/showDetailCommentConversation bản cũ.
   */
  showDetail<T = IShowDetailData>(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi<T>>('/chat/showdetail', {
      ...params
    });
  }

  /**
   * Tải tin nhắn cũ hơn (phân trang ngược).
   * BE: `showDetail?act=loadOlderMsg`, param: `id` + `after` (Elasticsearch search_after array).
   * - Lần đầu: `after = message.next` từ showDetail response (array sort values).
   * - Lần sau: `after = message.nextOlder` từ loadOlderMsg response (array sort values).
   * Response: `{ code: 1, data: ILoadDirectionalMessagesData }`
   */
  loadOlderMessages(params: { id: string; after?: any[] | null }) {
    return this.postRequest<IResponseApi<ILoadDirectionalMessagesData>>(
      '/chat/showdetail?act=loadOlderMsg',
      {...params}
    );
  }

  /**
   * Tải tin nhắn mới hơn khi đang xem một đoạn lịch sử ở giữa hội thoại
   * (thường sau khi nhảy tới kết quả tìm kiếm).
   */
  loadNewerMessages(params: { id: string; after?: any[] | null }) {
    return this.postRequest<IResponseApi<ILoadDirectionalMessagesData>>(
      '/chat/showdetail?act=loadNewerMsg',
      {...params}
    );
  }

  /**
   * Lấy danh sách toàn bộ nhãn (tags) của doanh nghiệp.
   * Tương đương bản cũ: db.getAllTags() → POST /business/tag/all
   * Response: { items: ITag[], pageGroupMapping?: any[] }
   */
  getAllTags() {
    return this.postRequest<IResponseApi>('/business/tag/all', {});
  }

  /**
   * Mute/Unmute hội thoại.
   * Tương đương bản cũ: db.handleMutedConversation()
   * @param act 'mute' | 'unmute'
   * @param params { pageId, id, muteType? }
   */
  handleMutedConversation(act: string, params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>(`/chat/showdetail?act=${act}`, {
      ...params
    });
  }

  /**
   * Pull tin nhắn từ sàn khi hội thoại mới chưa có nội dung.
   * Các kênh hỗ trợ: Shopee, Lazada, TikTok Shop và TikTok Business.
   */
  pullMessages(params: Record<string, unknown>, channel: number) {
    let url = '';
    if (channel === CHANNEL.SHOPEE) url = '/chat/shopee/pullmessages';
    else if (channel === CHANNEL.LAZADA) url = '/chat/lazada/pullmessages';
    else if (channel === CHANNEL.TIKTOK) url = '/chat/tiktok/pullmessages';
    else if (channel === CHANNEL.TIKTOK_BUSINESS) url = '/chat/tiktokbusiness/pullmessages';
    if (!url) {
      console.warn('[ConversationApiService] pullMessages: kênh', channel, 'không hỗ trợ pull.');
      // Không gửi request với URL rỗng: khi baseUrlApi trùng origin, request này
      // có thể trả về HTML của SPA và bị ErrorInterceptor hiểu sai là lỗi session.
      return throwError(() => new Error(`Unsupported pull-messages channel: ${channel}`));
    }
    return this.postRequest<IResponseApi>(url, {...params, channel});
  }

  /**
   * Tải lại nội dung hội thoại — chỉ lấy delta (tin nhắn mới + tin nhắn đã thay đổi).
   * Tương đương FE13 db.refreshMsg().
   * Mỗi channel có endpoint riêng.
   * Response (sau normalizeV1Response): { code: 1, data: { update: any[], items: any[] } }
   * - update: danh sách tin nhắn đã thay đổi (patch vào danh sách hiện tại theo id)
   * - items: danh sách tin nhắn mới (append vào danh sách, concat + sort)
   */
  refreshMsg(params: Record<string, unknown>, channel: number) {
    let url = '/chat/shopee/refreshmsg';
    switch (channel) {
      case CHANNEL.LAZADA:
        url = '/chat/lazada/refreshmsg';
        break;
      case CHANNEL.FACEBOOK:
        url = '/chat/facebook/refreshmsg';
        break;
      case CHANNEL.ZALO:
        url = '/chat/zalo/refreshmsg';
        break;
      case CHANNEL.INSTAGRAM:
        url = '/chat/instagram/refreshmsg';
        break;
      case CHANNEL.ZALO_PERSONAL:
        url = '/chat/zalopersonal/getoldmessage?act=group';
        (params as any).isRefresh = true;
        break;
      case CHANNEL.TIKTOK:
        url = '/chat/tiktok/pullmessages';
        (params as any).isRefresh = true;
        break;
      case CHANNEL.TIKTOK_BUSINESS:
        url = '/chat/tiktokbusiness/pullmessages';
        (params as any).isRefresh = true;
        break;
      case CHANNEL.WHATSAPP:
        url = '/chat/whatsapp/refreshmsg';
        break;
    }
    return this.postRequest<IResponseApi>(url, params);
  }

  /**
   * Cập nhật threadId cho Facebook.
   */
  updateThread(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/facebook/updatethread', params);
  }

  /**
   * Lấy danh sách đơn hàng nháp của hội thoại.
   */
  getOrderDraftList(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/orderdraftlist', params);
  }

  /**
   * Gửi sự kiện thủ công lên Meta CAPI. BE tự lấy channel, page, dataset và user data
   * từ conversationId; endpoint hiện dùng chung controller Facebook cho các kênh Meta.
   */
  createMetaOrder(params: ICreateMetaOrderParams) {
    return this.postRequest<IResponseApi<{ message?: string }>>('/chat/facebook/createorder', params);
  }

  /**
   * Cập nhật trạng thái đơn hàng nháp.
   * Tương đương FE13: db.updateOrderDraft({ id, status })
   */
  updateOrderDraft(params: { id: string; status: number }) {
    return this.postRequest<IResponseApi>('/chat/updateorderdraft', params);
  }

  /**
   * Lấy đơn hàng từ POS (hệ thống Nhanh.vn)
   */
  getOrderFromPos(params: {
    businessId: string | number;
    channel: number;
    shopId: string | number;
    buyerUserId: string | number;
  }) {
    return this.postRequest<IResponseApi>('/business/customer/getorderfrompos', params);
  }

  /**
   * Lấy danh sách bài viết đã đồng bộ theo pageId (FB/IG/Threads).
   * Tương đương bản cũ: db.postmanage({ pageIds, after, isLive, isReels, postId })
   *
   * BE trả response dạng V1: `{ scroller: { items, next } }` hoặc V2: `{ code: 1, data: { scroller: { items, next } }, messages: [] }`.
   * Ở đây unwrap scroller để component luôn nhận `{ items, next }` trực tiếp.
   */
  getPostsByPage(params: Record<string, unknown>) {
    return this.postRequest<any>('/chat/facebook/postmanage', params).pipe(
      map((res: any) => {
        // Xử lý cả V1 (res.scroller) và V2 (res.data.scroller)
        const scroller = res?.scroller || res?.data?.scroller;
        if (scroller) {
          return {...res, items: scroller.items ?? [], next: scroller.next ?? null};
        }
        return res;
      })
    );
  }

  /** Đồng bộ bài viết Threads vào DB. Không đồng bộ replies theo khoảng thời gian. */
  syncThreadsPosts(params: {fanpageId: string; postId?: string; syncType?: 'posts'}) {
    // Kèm postId: `post` là bài vừa tải lại (URL ảnh/video mới), null nếu Meta không trả.
    return this.postV2Request<IResponseApi<{posts: number; post?: IPostItem | null; postIds?: string[]}>>(
      '/chat/threads/syncpost',
      params,
    );
  }

  /**
   * Tải lại toàn bộ replies của một bài viết Threads đã có trong DB.
   * `syncpost` kèm `postId` chỉ cập nhật bài, không kéo replies.
   */
  syncThreadsComments(params: {fanpageId: string; postId: string}) {
    return this.postV2Request<IResponseApi<{replies: number}>>('/chat/threads/synccomment', params);
  }

  /**
   * Tải lại replies của một bài viết Threads trong khoảng thời gian đã chọn.
   * Server tự xử lý phân trang, FE không truyền cursor.
   */
  pullThreadsComments(params: {
    fanpageId: string;
    postId: string;
    since: number;
    until: number;
    currentTime?: number;
  }) {
    return this.postV2Request<IResponseApi>('/chat/threads/pullcomment', params);
  }

  /** Tạo một bài viết Threads (text và/hoặc một ảnh/video công khai). */
  createThreadsPost(params: {
    fanpageId: string;
    text?: string;
    imageUrl?: string;
    videoUrl?: string;
    crossShareToIg?: boolean;
    replyControl?: string;
    enableReplyApprovals?: boolean;
    locationId?: string;
  }) {
    return this.postV2Request<IResponseApi<IThreadsPublishResult>>('/chat/threads/createpost', params);
  }

  /** Tạo bài viết Threads nhiều ảnh/video (Carousel). */
  createThreadsCarouselPost(params: {
    fanpageId: string;
    text?: string;
    mediaItems: string; // JSON string: [{type: 'IMAGE'|'VIDEO', url: string}]
    crossShareToIg?: boolean;
    replyControl?: string;
    enableReplyApprovals?: boolean;
    locationId?: string;
  }) {
    return this.postV2Request<IResponseApi<IThreadsPublishResult>>('/chat/threads/createcarouselpost', params);
  }

  /** Publish tiếp container Threads chưa xử lý xong media (thường là video). */
  publishThreadsContainer(params: {fanpageId: string; containerId: string}) {
    return this.postV2Request<IResponseApi<IThreadsPublishResult>>('/chat/threads/publishcontainer', params);
  }

  /** Hồ sơ tài khoản Threads đang kết nối: avatar, tiểu sử, tick xanh. */
  getThreadsProfile(params: {fanpageId: string}) {
    return this.postV2Request<IResponseApi<IThreadsProfile>>('/chat/threads/profile', params);
  }

  /** Chỉ số một bài Threads: views, likes, replies, reposts, quotes, shares. */
  getThreadsPostInsights(params: {fanpageId: string; postId: string}) {
    return this.postV2Request<IResponseApi<IThreadsPostInsights>>('/chat/threads/insights', params);
  }

  /** Chỉ số tài khoản Threads trong khoảng since/until (Unix giây). */
  getThreadsAccountInsights(params: {fanpageId: string; since: number; until: number}) {
    return this.postV2Request<IResponseApi<IThreadsAccountInsights>>('/chat/threads/accountinsights', params);
  }

  /** Bài công khai có nhắc đến tài khoản Threads. */
  getThreadsMentions(params: {fanpageId: string; after?: string}) {
    return this.postV2Request<IResponseApi<IThreadsMediaPage>>('/chat/threads/mentions', params);
  }

  /** Tìm bài Threads công khai theo từ khóa hoặc chủ đề (tag). */
  searchThreads(params: {
    fanpageId: string;
    q: string;
    searchType: 'TOP' | 'RECENT';
    searchMode: 'KEYWORD' | 'TAG';
    mediaType?: 'TEXT' | 'IMAGE' | 'VIDEO';
    after?: string;
  }) {
    return this.postV2Request<IResponseApi<IThreadsMediaPage>>('/chat/threads/search', params);
  }

  /** Từ khoá tìm gần đây của tài khoản Threads (mới nhất trước), dùng làm gợi ý tìm kiếm. */
  getThreadsRecentSearches(params: {fanpageId: string}) {
    return this.postV2Request<IResponseApi<{keywords: string[]}>>('/chat/threads/recentsearches', params);
  }

  /** Tìm địa điểm để gắn vào bài viết Threads. */
  searchThreadsLocations(params: {fanpageId: string; q: string}) {
    return this.postV2Request<IResponseApi<IThreadsLocation[]>>('/chat/threads/locationsearch', params);
  }

  /** Tra cứu hồ sơ Threads công khai theo username. */
  lookupThreadsProfile(params: {fanpageId: string; username: string}) {
    return this.postV2Request<IResponseApi<IThreadsPublicProfile>>('/chat/threads/profilelookup', params);
  }

  /** Bài công khai gần đây của một hồ sơ Threads. */
  getThreadsProfilePosts(params: {fanpageId: string; username: string; after?: string}) {
    return this.postV2Request<IResponseApi<IThreadsMediaPage>>('/chat/threads/profileposts', params);
  }

  /** Trả lời công khai bài của người khác (bài nhắc đến, kết quả tìm kiếm). */
  replyThreadsMedia(params: {fanpageId: string; mediaId: string; text: string}) {
    return this.postV2Request<IResponseApi<{id: string; permalink: string | null}>>('/chat/threads/replymedia', params);
  }

  /** Xóa vĩnh viễn bài viết Threads và phản hồi đã lưu cục bộ. */
  deleteThreadsPost(params: {fanpageId: string; postId: string}) {
    return this.postV2Request<IResponseApi>('/chat/threads/deletepost', params);
  }

  /**
   * [FE13 syncPost] Đồng bộ nội dung bài viết từ Facebook/Instagram/Threads.
   * Gọi API để lấy chi tiết bài viết (message, story, attachments, images).
   * Endpoint: POST /chat/facebook/syncpost
   * @param params { fanpageId, postId, channel }
   */
  syncPostDetail(params: { fanpageId: string; postId: string; channel: number }) {
    let url = '';
    switch (params.channel) {
      case CHANNEL.INSTAGRAM:
        url = '/chat/instagram/syncpost';
        break;
      case CHANNEL.TIKTOK_BUSINESS:
        url = '/chat/tiktokbusiness/syncpost';
        break;
      case CHANNEL.THREADS:
        url = '/chat/threads/syncpost';
        break;
      default:
        url = '/chat/facebook/syncpost';
        break;
    }
    return this.postRequest<IResponseApi>(url, params);
  }

  /**
   * Resolve bài viết Instagram gốc từ ad ID của một tin nhắn quảng cáo.
   * BE trả `data.post` khi đã tìm được hoặc đã đồng bộ được post vào DB.
   */
  getInstagramPostIdFromAdId(params: {pageId: string; msgId: string; ad_id: string}) {
    return this.postRequest<IResponseApi<{post?: IPostItem}>>(
      '/chat/instagram/syncpost?act=getPostIdFromAdId',
      params,
    );
  }

  /**
   * Resolve attachment bài viết Facebook từ ad ID của message.
   * Khác Instagram: BE trả attachment đã được thay thế, không phải `data.post`.
   */
  getFacebookPostIdFromAdId(params: {pageId: string; msgId: string; ad_id: string}) {
    return this.postRequest<IResponseApi>('/chat/facebook/getpostidfromadid', params);
  }

  /**
   * Lấy lịch sử chỉnh sửa / thu hồi của một tin nhắn.
   * Tương đương bản cũ: db.logViewConversation({ type: 2, conversationId, messageId })
   * @param params { type: 2, conversationId, messageId }
   */
  logViewConversation(params: { type: number; conversationId: string; messageId?: string }) {
    return this.postRequest<IResponseApi>('/chat/logviewconversation', params);
  }

  /**
   * [§4.9] Xem log queue của kịch bản chăm sóc / gửi hàng loạt cho một hội thoại.
   * BE: POST /chatbot/scriptschedule/viewlogqueue
   */
  viewScriptQueue(params: {
    conversationId: string;
    businessId: string | number;
    scriptScheduleId?: string;
    after?: string;
  }) {
    return this.postRequest<IViewScriptQueueResponse>('/chatbot/scriptschedule/viewlogqueue', {...params});
  }

  listAutoOrderWebhookLogs(params: {
    conversationId: string;
    pageId: string;
    businessId: string | number;
    type: number;
  }) {
    return this.postRequest<IResponseApi<unknown>>('/api/auto/listwebhooklogs', params);
  }

  /**
   * Lấy danh sách tin nhắn của một hội thoại nhánh cũ.
   * Tương đương bản cũ: db.getMessages({ cid, pageId, channel })
   * @param params { cid: conversationId, pageId, channel? }
   */
  getOldConversationMessages(params: { cid: string; pageId: string; channel?: number }) {
    return this.postRequest<IResponseApi>('/chat/messages', {...params});
  }

  /**
   * Upload file (ảnh, audio...) lên cloud storage.
   * Tương đương FE1: db.upload2cloud({ businessId, fileName, file, metadata })
   * @param noSave true = gửi tin (không lưu thư viện), false/null = lưu vào thư viện media
   * @param pageId Có giá trị thì lưu theo page; không có thì lưu theo doanh nghiệp.
   */
  upload2cloud(
    businessId: number | string,
    fileName: string,
    file: File,
    noSave?: boolean,
    metadata?: string,
    pageId?: string,
  ) {
    const formData = new FormData();
    formData.append('businessId', String(businessId));
    // Backend cũ mặc định scope page. Luôn gửi `type` rõ ràng để không thể tạo
    // URL `/page//...` khi caller chỉ có businessId.
    if (pageId) {
      formData.append('pageId', pageId);
      formData.append('type', String(ATTACHMENT_UPLOAD_SCOPE.PAGE));
    } else {
      formData.append('type', String(ATTACHMENT_UPLOAD_SCOPE.BUSINESS));
    }
    if (noSave) formData.append('action', 'no-save');
    formData.append('fileName', fileName);
    formData.append('file', file);
    if (metadata) formData.append('metadata', metadata);
    return this.postFormDataRequest<any>('/business/attachment/upload2cloud', formData);
  }

  /**
   * Upload ảnh/file lên Facebook Graph API (để lấy faceAttachmentId).
   * FE1: db.uploadAttachmentFb()
   * Hỗ trợ 2 format: single {url, fanpageId} hoặc batch {urls: string[], fanpageId}.
   */
  uploadAttachmentFb(params: { url?: string; urls?: string[]; fanpageId: string; returnType?: boolean }) {
    const formData = new FormData();
    if (params.url) formData.append('url', params.url);
    if (params.urls) formData.append('urls', params.urls.join(','));
    formData.append('fanpageId', params.fanpageId);
    if (params.returnType) formData.append('returnType', '1');
    return this.postFormDataRequest<any>('/chat/facebook/upload', formData);
  }

  /** Upload file lên Zalo OA để nhận token `faceAttachmentId` trước khi gửi. */
  uploadAttachmentZalo(params: {fanpageId: string; file: File}) {
    const formData = new FormData();
    formData.append('fanpageId', params.fanpageId);
    formData.append('file', params.file, params.file.name);
    return this.postFormDataRequest<any>('/chat/zalo/upload', formData);
  }

  /**
   * Proxy tải file thư viện qua BE để tránh CORS, dùng riêng cho Zalo OA.
   * Zalo chỉ nhận upload file nhị phân, không nhận trực tiếp URL cloud.
   */
  downloadZaloAttachment(params: {fanpageId: string; fileUrl: string}): Observable<Blob> {
    const body = parseParams(this.getParamsCommon({...params}));
    return this.httpClient.post(this.getBaseUrlApi('/chat/zalo/upload'), body, {
      responseType: 'blob',
    });
  }

  /** Cập nhật thông tin KH (tên, địa chỉ, ngày sinh, giới tính…). */
  updateCustomer(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/business/customer/updatecustomer', params);
  }

  /** Danh sách ghi chú hội thoại. */
  getConversationNotes(params: { customerId: string; pageId: string }) {
    return this.postRequest<IResponseApi>('/chat/conversationnotes', {
      type: 'list',
      ...params,
    });
  }

  /** Tạo ghi chú hội thoại. */
  createConversationNote(params: {
    customerId: string;
    pageId: string;
    content: string;
    color?: string;
    attachments?: any[];
    configs?: string | null;
  }) {
    return this.postRequest<IResponseApi>('/chat/conversationnotes', {
      type: 'add',
      ...params,
    });
  }

  /** Sửa ghi chú hội thoại. */
  editConversationNote(params: {
    id: string;
    pageId: string;
    content?: string;
    configs?: string | null;
    attachments?: any[];
    removeAts?: number;
  }) {
    return this.postRequest<IResponseApi>('/chat/conversationnotes', {
      type: 'edit',
      ...params,
    });
  }

  /** Xóa ghi chú hội thoại. */
  deleteConversationNote(params: { noteIds: string[]; pageId: string; conversationId?: string }) {
    return this.postRequest<IResponseApi>('/chat/conversationnotes', {
      type: 'remove',
      ...params,
    });
  }

  /** Lấy lịch sử xem hội thoại theo endpoint logviewconversation. */
  getSeenHistory(params: {
    conversationId: string;
    type?: number;
    after?: string;
  }) {
    return this.postRequest<IResponseApi>('/chat/logviewconversation', params);
  }

  /** Lấy log hoạt động gắn/gỡ nhãn của hội thoại. */
  logTagActivity(params: { conversationId: string; type?: number; after?: string }) {
    return this.postRequest<IResponseApi>('/chat/logtagactivity', params);
  }

  /** Lấy danh sách nhân viên có thể giao hội thoại. */
  getAssignableStaff(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/index/assignablereceivers', params);
  }

  /** Lấy danh sách nhóm chia hội thoại theo business. */
  loadSplitGroups(params: { businessIds: Array<string | number> }) {
    return this.postRequest<IResponseApi>('/user/loadsplitgroup', params);
  }

  /**
   * Lấy danh sách nhân viên quản lý (chia hội thoại) theo business.
   * Tương đương bản cũ: db.getUserManageSplit({ businessIds, limit })
   */
  getUserManageSplit(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/business/user/manageusersplit', params);
  }

  /** Giao hội thoại cho nhân viên / nhóm. */
  assignConversation(params: {
    conversationId: string;
    pageId: string;
    assigneeIds?: string[];
    assignedGroupId?: string;
    removeGroupId?: boolean;
    noRemoveGroup?: boolean;
    removeAssigneeIds?: boolean | number;
  }) {
    const payload: Record<string, unknown> = {
      id: params.conversationId,
      fanpageId: params.pageId,
      updateSplit: true,
    };

    if (params.assigneeIds) {
      // Khi danh sách nhân viên rỗng (người dùng bỏ chọn tất cả): gửi cờ removeAssigneeIds = 1 theo chuẩn Backend VPage
      if (params.assigneeIds.length === 0) {
        payload['removeAssigneeIds'] = 1;
      } else {
        payload['editAssigneeIds'] = params.assigneeIds;
      }
    }
    if (params.removeAssigneeIds) {
      payload['removeAssigneeIds'] = 1;
    }
    if (params.assignedGroupId) {
      payload['editGroupId'] = params.assignedGroupId;
    }
    if (params.removeGroupId) {
      payload['removeGroupId'] = 1;
    }
    if (params.noRemoveGroup) {
      payload['noRemoveGroup'] = 1;
    }

    return this.postRequest<IResponseApi>('/chat/updateviewconversation', payload);
  }

  /** Update the conversation's system assignee flags without changing staff assignment. */
  updateConversationAssigneeFlags(params: {
    conversationId: string;
    businessId: string | number;
    stopSplit?: 1 | 2;
    allowAllStaffView?: 1 | 2;
  }) {
    const payload: Record<string, unknown> = {
      id: params.conversationId,
      businessId: params.businessId,
    };
    if (params.stopSplit !== undefined) payload['stopSplit'] = params.stopSplit;
    if (params.allowAllStaffView !== undefined) payload['allowAllStaffView'] = params.allowAllStaffView;
    return this.postRequest<IResponseApi>('/chat/updateviewconversation', payload);
  }

  /** Gửi thông tin ngân hàng dưới dạng tin nhắn văn bản đến KH. */
  sendBankInfo(params: {
    conversationId: string;
    pageId: string;
    channel: number;
    recipientId: string;
    /** Loại hội thoại, mặc định INBOX (Zalo cá nhân nhóm truyền GROUP) */
    type?: number;
    bankData: Record<string, unknown>;
  }) {
    const b = params.bankData;
    const lines = [
      b['bankName'] ? `Ngân hàng: ${b['bankName']}${b['bankBranch'] ? ` - ${b['bankBranch']}` : ''}` : '',
      b['accountNo'] ? `Số TK: ${b['accountNo']}` : '',
      b['accountName'] ? `Chủ TK: ${b['accountName']}` : '',
      b['amount'] ? `Số tiền: ${Number(b['amount']).toLocaleString('vi-VN')} VNĐ` : '',
      b['note'] ? `Nội dung CK: ${b['note']}` : '',
    ].filter(Boolean);
    return this.sendMessage({
      type: params.type ?? CONVERSATION_TYPE.INBOX,
      fanpageId: params.pageId,
      conversationId: params.conversationId,
      channel: params.channel,
      recipientId: params.recipientId,
      text: lines.join('\n'),
    });
  }

  /** Danh sách tài khoản ngân hàng doanh nghiệp đã lưu. */
  getBankAccounts(params: { businessId: string | number; pageId: string }) {
    return this.postRequest<IResponseApi>(uriConsts.chat.BANK_ACCOUNT, {...params});
  }

  /** Thêm tài khoản ngân hàng. */
  addBankAccount(params: {
    businessId: string | number;
    pageId: string;
    bankCode: string;
    binCode: string;
    accountNumber: string;
    accountName: string;
  }) {
    return this.postRequest<IResponseApi<IAddBankAccountResponse>>('/chat/updatebankaccount?act=add', {...params});
  }

  /** Cập nhật tài khoản ngân hàng. */
  updateBankAccount(params: {
    businessId: string | number;
    pageId: string;
    id: string | number;
    bankCode: string;
    binCode: string;
    accountNumber: string;
    accountName: string;
  }) {
    return this.postRequest<IResponseApi<IAddBankAccountResponse>>('/chat/updatebankaccount?act=update', {...params});
  }

  /** Xóa tài khoản ngân hàng. */
  deleteBankAccount(params: { businessId: string | number; pageId: string; id: string | number }) {
    return this.postRequest<IResponseApi>('/chat/deletebankaccount', {...params});
  }

  /** Yêu cầu địa chỉ giao hàng từ KH (chỉ Facebook). Gửi qua replyinbox với cờ requestAddress. */
  requestAddress(params: {
    conversationId: string;
    pageId: string;
    channel: number;
    recipientId: string;
  }) {
    return this.postRequest<IResponseApi>('/chat/facebook/replyinbox', {
      ...params,
      fanpageId: params.pageId,
      requestAddress: true,
      type: CONVERSATION_TYPE.INBOX,
    });
  }

  /** Danh sách địa chỉ doanh nghiệp đã lưu để chia sẻ cho khách. */
  listBusinessAddresses(params: { businessId: string | number; pageId: string }) {
    return this.postRequest<IResponseApi>('/chat/address', {...params});
  }

  /** Thêm địa chỉ doanh nghiệp. */
  addBusinessAddress(params: {
    businessId: string | number;
    pageId: string;
    name: string;
    address: string;
    link: string;
  }) {
    return this.postRequest<IResponseApi>('/chat/updateaddress?act=add', {...params});
  }

  /** Cập nhật địa chỉ doanh nghiệp. */
  updateBusinessAddress(params: {
    businessId: string | number;
    pageId: string;
    addressId: string | number;
    name: string;
    address: string;
    link: string;
  }) {
    return this.postRequest<IResponseApi>('/chat/updateaddress?act=update', {...params});
  }

  /** Xóa địa chỉ doanh nghiệp. */
  deleteBusinessAddress(params: {
    businessId: string | number;
    pageId: string;
    addressId: string | number;
  }) {
    return this.postRequest<IResponseApi>('/chat/deleteaddress', {...params});
  }

  /** Chia sẻ vị trí dưới dạng tin nhắn văn bản + link Google Maps. */
  shareLocation(params: {
    conversationId: string;
    pageId: string;
    channel: number;
    recipientId: string;
    latitude: number;
    longitude: number;
    address?: string;
  }) {
    const lines = [
      `📍 Vị trí: ${params.latitude}, ${params.longitude}`,
      ...(params.address ? [params.address] : []),
      `https://www.google.com/maps?q=${params.latitude},${params.longitude}`,
    ];
    return this.sendMessage({
      type: CONVERSATION_TYPE.INBOX,
      fanpageId: params.pageId,
      conversationId: params.conversationId,
      channel: params.channel,
      recipientId: params.recipientId,
      text: lines.join('\n'),
    });
  }

  /** Gắn / gỡ nhãn khỏi hội thoại. */
  toggleConversationTag(params: {
    action: 'add' | 'remove';
    conversationId: string;
    pageId: string;
    tagId: string;
  }) {
    return this.postRequest<IResponseApi>('/chat/toggletag', params);
  }

  /** Bật / tắt BizAI owner cho hội thoại. BE: POST /chat/showdetail?act=toggleAIOwner */
  toggleAIOwner(params: { id: string; pageId: string; action: 'on' | 'off' }) {
    return this.postRequest<IResponseApi>('/chat/showdetail?act=toggleAIOwner', params);
  }

  /** Cập nhật thông tin user Zalo OA (bao gồm alias). BE: POST /chat/zalo/updateuserdetails */
  updateZaloUserDetails(params: { pageId: string; userId: string; alias?: string; [key: string]: unknown }) {
    return this.postRequest<IResponseApi>('/chat/zalo/updateuserdetails', params);
  }

  /**
   * [§4.2] Lấy danh sách mẫu tin nhắn tiện ích.
   * BE: POST /chatbot/utilitymessage/list
   * Response: { code: 1, data: IUtilityTemplateRaw[], after: null }
   */
  listUtilityTemplates(params: {
    businessId: string | number;
    pageId?: string;
    pageIds?: string[];
    applicablePageIds?: string[];
    active?: number;
    status?: number;
    type?: 1 | 2;
  }) {
    return this.postRequest<IResponseApi>('/chatbot/utilitymessage/list', {...params});
  }

  /**
   * [§4.3] Lấy danh sách kịch bản chatbot cho một page.
   * BE: POST /chat/loadblockconversation
   * Response: { items: IChatbotBlock[] } hoặc { error: string }
   */
  loadBlockConversation(params: { fanpageId: string }) {
    return this.postRequest<IResponseApi>('/chat/loadblockconversation', {...params});
  }

  /**
   * [§4.3] Gửi kịch bản chatbot vào hội thoại.
   * BE: POST /chat/sendblockconversation
   * Response: { code: 1 } hoặc { error: string }
   */
  sendBlockConversation(params: { blockId: string; conversationId: string; fanpageId: string }) {
    return this.postRequest<IResponseApi>('/chat/sendblockconversation', {...params});
  }

  /**
   * [§4.4] Cập nhật cài đặt tạm ngừng chatbot / kịch bản cho hội thoại.
   * BE: POST /chat/updateconversationchatbot — bắt buộc có cả stopBot và stopScript (1 = tạm ngừng, 2 = đang chạy).
   */
  updateConversationChatbot(params: {
    conversationId: string;
    stopBot: number;
    noExpiry: number;
    botPauseExpiresAt: number;
    stopScript: number;
    scriptPauseExpiresAt: number;
    noScriptExpiry: number;
  }) {
    // BE trả `settings` đầy đủ của hội thoại sau khi lưu.
    return this.postRequest<IResponseApi<{ settings?: Record<string, any> }>>('/chat/updateconversationchatbot', {...params});
  }

  /**
   * [§4.16] Đếm số lượng bài viết theo điều kiện lọc.
   * BE: POST /chat/facebook/countpost
   */
  countPost(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/facebook/countpost', {...params});
  }

  /**
   * [§4.16] Lấy thống kê hội thoại theo danh sách bài viết.
   * BE: POST /chat/facebook/postcount
   * Response: { totalConversation: Record<postId, number>, totalConversationHasPhone: Record<postId, number> }
   */
  getPostCount(params: { postIds: string[] }) {
    return this.postRequest<IResponseApi>('/chat/facebook/postcount', {...params});
  }

  /**
   * [§4.16] Tải bình luận từ Facebook hoặc Instagram theo bài viết.
   * BE: POST /chat/facebook/pullcomments | /chat/instagram/pullcomments
   */
  pullComments(params: Record<string, unknown>, channel: number) {
    const url = channel === CHANNEL.INSTAGRAM
      ? '/chat/instagram/pullcomments'
      : '/chat/facebook/pullcomments';
    return this.postRequest<IResponseApi>(url, {...params});
  }

  /**
   * [§4.17] Tải lại hội thoại inbox theo kênh và page.
   * BE: POST /chat/{channel}/pullinboxs (FB, IG, Shopee, Lazada, Zalo, Zalo Personal, TikTok)
   */
  pullInboxs(params: Record<string, unknown>, channel: number) {
    const urlMap: Record<number, string> = {
      [CHANNEL.FACEBOOK]: '/chat/facebook/pullinboxs',
      [CHANNEL.INSTAGRAM]: '/chat/instagram/pullinboxs',
      [CHANNEL.SHOPEE]: '/chat/shopee/pullinboxs',
      [CHANNEL.LAZADA]: '/chat/lazada/pullinboxs',
      [CHANNEL.ZALO]: '/chat/zalo/pullinboxs',
      [CHANNEL.ZALO_PERSONAL]: '/chat/zalopersonal/getoldmessage',
      [CHANNEL.TIKTOK]: '/chat/tiktok/pullinboxs',
      [CHANNEL.TIKTOK_BUSINESS]: '/chat/tiktokbusiness/pullinboxs',
      [CHANNEL.WHATSAPP]: '/chat/whatsapp/pullinboxs',
    };
    const url = urlMap[channel] ?? '/chat/facebook/pullinboxs';
    return this.postRequest<IResponseApi>(url, {...params});
  }

  /**
   * [§4.17] Tải lại bài viết Facebook để lấy pull comments.
   * BE: POST /chat/facebook/pullPost
   */
  pullPost(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/facebook/pullPost', {...params});
  }

  /**
   * [§4.16] Ẩn bình luận từ Facebook hoặc Instagram theo bài viết.
   * BE: POST /chat/facebook/hidecomments | /chat/instagram/hidecomments
   */
  hideComments(params: Record<string, unknown>, channel: number) {
    const url = channel === CHANNEL.INSTAGRAM
      ? '/chat/instagram/hidecomments'
      : '/chat/facebook/hidecomments';
    return this.postRequest<IResponseApi>(url, {...params});
  }

  /**
   * [§4.16] Xóa một mục báo xấu khỏi danh sách.
   * BE: POST /business/customer/removebadcustomer
   */
  removeBadCustomer(params: { fanpageId: string; id: string }) {
    return this.postRequest<IResponseApi>('/business/customer/removebadcustomer', {...params});
  }

  // ─── [§4.1] Marketing OTN ──────────────────────────────────────────────────

  /**
   * [§4.1] Lấy danh sách chủ đề marketing (OTN topics) của một business/page.
   * BE: POST /chatbot/marketing/listtopic (v2 only)
   */
  listMarketingTopics(params: { businessId: number; pageId?: string; next?: string; size?: number }) {
    return this.postRequest<IResponseApi<IListMarketingTopicsData>>('/chatbot/marketing/listtopic', {...params});
  }

  // ─── [§4.6] Product dialogs ────────────────────────────────────────────────

  /**
   * [§4.6] Lấy danh sách catalog sản phẩm Facebook theo pageId.
   * BE: POST /chat/getcatalog
   * Response: { data: IProductCatalog[], error?: string }
   */
  getCatalog(params: { pageId: string }) {
    return this.postRequest<IGetCatalogResponse>(uriConsts.chat.GET_CATALOG, {...params});
  }

  /**
   * [§4.6] Lấy danh sách sản phẩm Meta theo catalogId (phân trang cursor).
   * BE: POST /chat/getmetaproducts
   * Response: { data: IMetaProductRaw[], cursors?: { before, after }, error?: string }
   */
  getMetaProducts(params: { catalogId: string; pageId: string; after?: string; name?: string }) {
    return this.postRequest<IGetMetaProductsResponse>(uriConsts.chat.GET_META_PRODUCTS, {...params});
  }

  /**
   * [§4.6] Lấy danh sách sản phẩm sàn Ecom (Shopee / Lazada / TikTok).
   * Tự động route đến endpoint đúng theo channel.
   * BE: POST /chat/shopee/getproductlist | /chat/lazada/getproductlist | /chat/tiktok/viewgeneral?act=getProductList
   */
  getEcomProductList(params: { pageId: string; next?: string | number }, channel: number) {
    const urlMap: Partial<Record<number, string>> = {
      [CHANNEL.SHOPEE]: '/chat/shopee/getproductlist',
      [CHANNEL.LAZADA]: '/chat/lazada/getproductlist',
      [CHANNEL.TIKTOK]: '/chat/tiktok/viewgeneral?act=getProductList',
    };
    const url = urlMap[channel] ?? '/chat/shopee/getproductlist';
    return this.postRequest<IGetEcomProductListResponse>(url, {...params});
  }

  /**
   * Lấy chi tiết các sản phẩm được đính kèm trong lịch sử hội thoại Shopee.
   * `items` là map `{[itemId]: messageIds[]}` để BE trả dữ liệu một lần cho cả batch.
   * BE: POST /chat/shopee/getitem
   */
  getShopeeItems(params: { channel: number; fanpageId: string; items: string }) {
    return this.postRequest<IResponseApi<Record<string, unknown>>>('/chat/shopee/getitem', {...params});
  }

  /** Lấy lịch sử hỏi đáp được gom thành một Shopee bundle message. */
  getShopeeBundleMessages(params: { pageId: string; cid: string; message: string; limit?: number }) {
    return this.postRequest<IResponseApi<unknown>>('/chat/shopee/getbundlemessages', {...params});
  }

  /** BE: POST /chat/tiktok/getproduct — lấy một product attachment của hội thoại TikTok. */
  getTikTokMessageProduct(params: { channel: number; fanpageId: string; msgId: string; itemId: string }) {
    return this.postRequest<IResponseApi<unknown>>('/chat/tiktok/getproduct', {...params});
  }

  /**
   * Lấy batch đơn hàng gắn trong lịch sử chat Ecom.
   * FE13 chỉ có endpoint cho Shopee và TikTok; Lazada dùng `order2` đã có payload.
   */
  getEcommerceMessageOrders(
    params: { channel: number; fanpageId: string; orders: string },
    channel: number,
  ) {
    const urlMap: Partial<Record<number, string>> = {
      [CHANNEL.SHOPEE]: '/chat/shopee/getorder',
      [CHANNEL.TIKTOK]: '/chat/tiktok/getorder',
    };
    const url = urlMap[channel];
    if (!url) return throwError(() => new Error(`Unsupported ecom-order channel: ${channel}`));
    return this.postRequest<IResponseApi<Record<string, unknown>>>(url, {...params});
  }

  /**
   * [§3.E] Lấy danh sách voucher sàn Ecom (Shopee / Lazada / TikTok).
   * BE: POST /chat/shopee/getvoucherlist | /chat/lazada/getvoucherlist | /chat/tiktok/viewgeneral?act=getCouponList
   */
  getEcomVoucherList(params: { pageId: string; conversationId: string }, channel: number) {
    const urlMap: Partial<Record<number, string>> = {
      [CHANNEL.SHOPEE]: '/chat/shopee/getvoucherlist',
      [CHANNEL.LAZADA]: '/chat/lazada/getvoucherlist',
      [CHANNEL.TIKTOK]: '/chat/tiktok/viewgeneral?act=getCouponList',
    };
    const url = urlMap[channel] ?? '/chat/shopee/getvoucherlist';
    return this.postRequest<IGetEcomVoucherListResponse>(url, {...params});
  }

  /**
   * [§4.6] Tìm kiếm sản phẩm Shopee từ POS.
   * BE: POST /chat/shopee/getproductfrompos
   */
  searchProductFromPos(params: {
    name: string;
    channel: number;
    shopId: string;
    businessId?: string;
    next?: string | number;
  }) {
    return this.postRequest<IGetProductFromPosResponse>(uriConsts.chat.SHOPEE_GET_PRODUCT_FROM_POS, {...params});
  }

  /**
   * [§4.1] Gửi yêu cầu OTN opt-in đến khách hàng trong hội thoại.
   * BE: POST /chatbot/marketing/sendoptin (v2 only)
   */
  sendMarketingOptIn(params: { conversationId: string; topicId: string; pageId: string }) {
    return this.postRequest<IResponseApi<{ rs: unknown }>>('/chatbot/marketing/sendoptin', {...params});
  }

  // ─── [§4.18] Block / Lock customer ──────────────────────────────────────────

  /**
   * [§4.18] Khoá/mở khoá hoặc chặn/bỏ chặn khách hàng trên Facebook/Zalo Personal.
   * action: undefined → lock/unlock; 2 → block/unblock
   * BE: POST /business/customer/lockcustomer
   */
  lockCustomer(params: { userId: string; fanpageId: string; customerId: string; action?: number }) {
    return this.postRequest<IResponseApi<ILockCustomerResponse>>('/business/customer/lockcustomer', {...params});
  }

  /**
   * [§4.18] Thêm báo xấu khách hàng.
   * BE: POST /business/customer/addbadcustomer
   */
  addBadCustomer(params: { mobile: string; reason: string; fanpageId: string; userDisplayName: string }) {
    return this.postRequest<IResponseApi>('/business/customer/addbadcustomer', {...params});
  }

  /**
   * [§4.18] Lấy danh sách báo xấu theo SĐT khách hàng.
   * FE13: DbService.getBadCustomer → POST /business/customer/viewbadcustomer
   * Params: { phones: string[], pageId: string }
   * Response: { items: IBadCustomerItem[] }
   */
  getBadCustomer(params: { phones: string[]; pageId: string }) {
    return this.postRequest<{ items: any[] }>('/business/customer/viewbadcustomer', {...params});
  }

  // ─── Quick Message search settings ─────────────────────────────────────────

  /**
   * Lưu cài đặt tìm kiếm tin nhắn nhanh (shortcut/title/message).
   * FE13: db.updateSettingQmsg → POST /user/updatesettingqmsg
   */
  updateSettingQmsg(params: { qmsg: string }) {
    return this.postRequest<IResponseApi>('/user/updatesettingqmsg', params);
  }

  /**
   * Lấy cài đặt cá nhân của user (bao gồm sortActivities, hideAdIcon, hideHandleName, searchQmsgMatch...).
   * FE13: db.userSetting → POST /user/setting (dùng POST để normalize V1 response).
   */
  getUserSetting() {
    return this.postRequest<IResponseApi>('/user/setting');
  }

  /** Lưu cài đặt cá nhân. FE13 dùng cùng endpoint với payload `setting` JSON. */
  updateUserSetting(params: {setting: string}) {
    return this.postRequest<IResponseApi>('/user/setting', params);
  }

  /** Kết nối Google Sheets: trả `url` đăng nhập Google; `unlink` = ngắt kết nối. */
  requestGooglePerms(unlink = false) {
    return this.postUserRequest<IResponseApi<{url?: string}>>('/auth/requestgoogleperms', unlink ? {unlink: 1} : {});
  }

  /**
   * Khởi tạo cache page-level settings cho trang hội thoại.
   * Gọi 1 lần khi vào trang hội thoại để preload settings của tất cả page.
   */
  getPageSettings(params: { pageIds?: string[]; checkBotId?: boolean } = {}) {
    // items: pageId → settings của page; PHP trả `[]` khi rỗng.
    return this.postRequest<IResponseApi<{ items?: Record<string, Record<string, unknown>> | unknown[] }>>('/chat/settingpages', params);
  }

  /**
   * Lấy dữ liệu scan session của extension cho các page.
   * Trả về danh sách scan session để populate extensionScanning map.
   */
  getScanSessions(params: { pageIds: string[] }) {
    return this.postRequest<IResponseApi>('/chat/getscansession', params);
  }

  /**
   * [§4.18] Kiểm tra thời gian cho phép chặn lại (replay ban) khách hàng.
   * BE: POST /business/customer/getchecktimebanreplay
   */
  getCheckReplayBan(params: { fanpageId: string; customerId: string }) {
    return this.postRequest<IResponseApi>('/business/customer/getchecktimebanreplay', {...params});
  }

  /**
   * [Extension] Cập nhật scan session extension.
   * BE: POST /chat/updatescansession
   */
  updateScanSessionExtension(params: { scanSession: any }) {
    return this.postRequest<IResponseApi>('/chat/updatescansession', params);
  }

  /**
   * [Extension] Cập nhật thread meta extension (mapping data).
   * BE: POST /chat/updatethreadmeta
   */
  updateThreadMetaExtension(params: { mappingData: string }) {
    return this.postRequest<IResponseApi>('/chat/updatethreadmeta', params);
  }

  /**
   * [Extension] Cập nhật emoji tin nhắn (khi extension gửi reaction thành công).
   * BE: POST /chat/facebook/updateemoji
   */
  updateEmojiMessage(params: { msgId: string; pageId: string; emoji: string }) {
    return this.postRequest<IResponseApi>('/chat/facebook/updateemoji', params);
  }

  /**
   * [§4.18] Lấy tin nhắn của hội thoại theo threadId (dùng cho comment-channel dialog).
   * BE: POST /chat/messages
   */
  getMessages<T = any>(params: { cid: string; pageId: string; channel?: number; threadId?: string; postId?: string; limit?: number }) {
    return this.postRequest<IResponseApi<T>>('/chat/messages', {...params});
  }

  // ─── [§4.8] Split conversation flow ─────────────────────────────────────────

  /**
   * [§4.8] Lấy log lịch sử chia hội thoại.
   * BE: POST /chat/logsplitconversation
   */
  logSplitConversation(params: { conversationId: string; limit?: number }) {
    return this.postRequest<IResponseApi>('/chat/logsplitconversation', {...params});
  }

  /**
   * [§4.8] Lấy thống kê quản lý chia hội thoại (avg manage split).
   * BE: POST /chat/getavgmangesplit
   */
  getAvgManageSplit(params: { mode: number; sort?: number; fromTime?: string; toTime?: string; businessId?: number }) {
    return this.postRequest<IResponseApi>('/chat/getavgmangesplit', {...params});
  }

  /**
   * [§4.8] Lấy danh sách hội thoại theo object (staffId/groupId).
   * BE: POST /chat/listconversationobject
   */
  getListConversationByObject(params: Record<string, unknown>) {
    return this.postRequest<IResponseApi>('/chat/listconversationobject', {...params});
  }

  /**
   * [§4.8] Lấy danh sách người dùng theo businessId.
   * BE: POST /business/user/list
   */
  getBusinessUsers(params: { limit?: number; businessId?: number }) {
    return this.postRequest<IResponseApi>('/business/user/list', {...params});
  }

  /**
   * [§4.8] Lấy danh sách người dùng doanh nghiệp phục vụ chat/chia hội thoại.
   * Tương đương bản cũ v1: DbService.loadUsersByBusinessId
   * BE: POST /auth/loadusers
   * @param params { businessIds?: (string | number)[]; businessId?: string | number; limit?: number }
   */
  loadUsersByBusinessId(params: ILoadUsersParams) {
    return this.postRequest<IResponseApi<IUserBusiness[]>>('/auth/loadusers', {...params});
  }

  /**
   * [§4.8] Lấy trạng thái active của nhân viên.
   * BE: POST /business/index/statusstaffactive
   */
  loadStatusActiveStaff(params: { ids: (string | number)[]; role?: number }) {
    return this.postRequest<IResponseApi>('/business/index/statusstaffactive', {...params});
  }

  /**
   * [§4.8] Chạy chia thủ công hội thoại (re-manual assign).
   * act: 'saveTypeCustom' | undefined
   * BE: POST /chat/remanualassigne
   */
  reManualAssign(params: Record<string, unknown>) {
    // Legacy BE requires the page ownership map, not just the active businessId.
    // Use all joined pages: the header may currently be filtering another business.
    const dataBussiness: Record<string, string[]> = {};
    for (const page of this.authService.getAllPages() || []) {
      if (page?.id == null) continue;
      const key = page.businessId && String(page.businessId) !== '0' ? String(page.businessId) : 'noBusiness';
      const ids = dataBussiness[key] ??= [];
      if (!ids.includes(String(page.id))) ids.push(String(page.id));
    }
    return this.postRequest<IResponseApi>('/chat/remanualassigne', {
      ...params,
      dataBussiness: JSON.stringify(dataBussiness),
    });
  }

  /**
   * Đánh dấu hội thoại đã đọc / đã trả lời theo thời gian.
   * BE: POST /chat/markReaded
   * FE1: db.updateMarkReaded({ selectedTime, isRead, isReply, postIds })
   */
  markReaded(params: {
    selectedTime: number;
    isRead: number;
    isReply: number;
    postIds: string[] | null;
  }) {
    return this.postRequest<IResponseApi>('/chat/markReaded', {...params});
  }

  /**
   * Gắn / gỡ nhãn tự động cho hội thoại.
   * BE: POST /chat/autotagging
   * FE1: db.autoTagging({ pageId, tags, type, skipTags })
   */
  autoTagging(params: {
    pageId: string;
    tags: string[];
    type: string;
    skipTags: string[] | null;
  }) {
    return this.postRequest<IResponseApi>('/chat/autotagging', {...params});
  }



  /**
   * [MSG-SEND-001] GAP-A: Tracking user report sau khi gửi tin thành công.
   * FE13: db.service.ts line 1161 — POST /report/user/tracking
   * FE13: db.trackingUserReport({ pid, cid, t, msgId, conversationId, customerSentAt })
   */
  trackingUserReport(params: ITrackingUserReportParams) {
    return this.postRequest<IResponseApi>('/report/user/tracking', {...params});
  }

  /**
   * [MSG-SEND-001] GAP-B: Tracking reply split conversation sau khi gửi tin.
   * BE: POST /user/trackingreplysplitconversation
   * FE13: db.trackingReplySplitConversation({ conversationId, splitType })
   */
  trackingReplySplitConversation(params: {
    conversationId: string;
    splitType: string;
    assignedGroupId?: string;
  }) {
    return this.postRequest<IResponseApi>('/user/trackingreplysplitconversation', {...params});
  }

  updatePageAvatar(params: {
    pageId: string;
  }) {
    return this.postRequest<IResponseApi<{
      avatar: string;
      name?: string;
      pageId: string;
    }>>('/chat/tiktokbusiness/index?act=updateAvatar', {...params});
  }

  /**
   * Kiểm tra quyền & trạng thái tham gia nhận chia hội thoại của nhân viên.
   * Dùng postUserRequest vì đây là API cấp User cá nhân, nhân viên không có quyền quản lý DN vẫn gọi được bình thường.
   * BE: POST /user/checkjoinconversation
   */
  checkJoinConversation(params: { data: string }) {
    return this.postUserRequest<IResponseApi>('/user/checkjoinconversation', {...params});
  }

  /**
   * Nhân viên click bắt đầu nhận hội thoại.
   * BE: POST /user/startreceivingconversation
   */
  startReceivingConversation() {
    return this.postUserRequest<IResponseApi>('/user/startreceivingconversation', {});
  }

  /**
   * Nhân viên click tạm ngừng nhận hội thoại.
   * BE: POST /user/stopreceivingconversation
   */
  stopReceivingConversation() {
    return this.postUserRequest<IResponseApi>('/user/stopreceivingconversation', {});
  }

  /** Tiến trình ngầm định kỳ (2 phút) kiểm tra & tự động chia lại hội thoại. BE: POST /chat/reassignee */
  intervalReAssignee(params: Record<string, unknown> = {}) {
    return this.postRequest<IResponseApi>('/chat/reassignee', {...params});
  }

  /** Chia lại hội thoại theo ID cụ thể (từ Socket/Queue). BE: POST /chat/reassigneeconversation */
  reAssigneeConversationByIds(params: { id: string; pageId: string }) {
    return this.postRequest<IResponseApi>('/chat/reassigneeconversation', {...params});
  }

  /** Lấy báo cáo lịch sử chia hội thoại chi tiết cho Dashboard. BE: POST /report/conversationsplitreport/getreport */
  getReportHistoryConversationSplit(params: {
    mode: number;
    businessId: number;
    fromTime?: string;
    toTime?: string;
    sort?: number | string;
    pageIds?: string[];
  }) {
    return this.postRequest<IResponseApi>('/report/conversationsplitreport/getreport', {...params});
  }
}
